/**
 * Regla de l'IVA per a qualsevol tipus — plans/preus-en-crear-reserves.md §2 (fase F1).
 *
 * Donat un PVP i un tipus d'IVA, diu si el preu es pot partir en base i quota al cèntim, i quins són
 * els preus bons més propers. El PMS AVISA i ensenya els dos veïns (decisió d'en Pere, 11-OCT-2026):
 * aquí no hi ha cap funció que arrodoneixi un preu tota sola.
 *
 * ── Dues còpies, un sol text ────────────────────────────────────────────────────────────────
 * Aquest fitxer viu a `packages/types/src/vat-split.ts` (el fa servir el frontend) i, IDÈNTIC, a
 * `apps/backend/src/modules/reservations/vat-split.ts` (el backend no pot importar valors de
 * `@guilleumes/types` en execució). `vat-split.parity.spec.ts` falla si deixen de ser iguals:
 * qualsevol canvi es fa en un i es copia sencer a l'altre.
 *
 * ── Cèntims enters ──────────────────────────────────────────────────────────────────────────
 * Tots els imports són ENTERS DE CÈNTIMS i el tipus, un enter de centèsimes de punt (10 % = 1000).
 * La regla només suma, resta, multiplica enters i en treu el residu; l'única divisió és la de
 * `divFloor`, que sempre divideix un múltiple exacte. Cap producte passa de 10^12, molt per sota
 * de `Number.MAX_SAFE_INTEGER`. La coma flotant només existeix dins de la frontera marcada més
 * avall (`toCents`, `centsToEuros` i `vatRateFromPercent`), que és l'única porta amb el `Float` en
 * euros de la reserva i amb un tipus de configuració escrit amb decimals. `vat-split.spec.ts`
 * llegeix aquest fitxer i falla si a la regla hi apareix un decimal, un `toFixed` o una altra
 * divisió.
 *
 * ── Els tres estats d'un PVP ────────────────────────────────────────────────────────────────
 * | Estat | Què vol dir                                                                        |
 * |-------|------------------------------------------------------------------------------------|
 * | `A`   | A la graella forta: múltiple del pas de PVP. Quadra multiplicat i sumat com vulguis  |
 * | `B`   | Exacte només com a total: hi ha una base entera que dona just aquest PVP             |
 * | `C`   | Sense base exacta: cap base hi arriba; sempre queda a un cèntim                      |
 *
 * ── Convencions fixades aquí (canviar-les és canviar aquest fitxer i els seus tests) ────────
 * | Convenció                                                                    | On               |
 * |------------------------------------------------------------------------------|------------------|
 * | Quota = tipus sobre la base TOTAL, arrodonida al cèntim, meitat amunt         | `roundedTax`     |
 * | Estat C: es torna la base més alta que NO es passa del PVP, i els cèntims que falten | `splitGross` |
 * | Els veïns són estrictes: mai el mateix PVP, encara que ja sigui bo            | `nearestOnGrid`  |
 * | Tipus: enter de 0 a 100, o `{ basisPoints }` de 0 a 10000 per als decimals    | `checkVatRate`   |
 * | Un tipus escrit «5.2» es passa a centèsimes de punt aquí, no a casa de qui crida | `vatRateFromPercent` |
 * | Sostre de 999.999,99 € per import (el mateix del nucli de cobraments)         | `MAX_AMOUNT_CENTS` |
 * | D'euros a cèntims: meitat amunt; «exacte» = a menys de 0,00005 cèntims d'un cèntim sencer | `toCents` |
 * | Un negatiu de debò (més enllà d'aquesta tolerància) és `NEGATIVE`, mai un zero | `toCents`        |
 *
 * ── Qui llança i qui no ─────────────────────────────────────────────────────────────────────
 * `splitGross`, `nearestOnGrid`, `vatGridSteps`, `vatOnBase` i `centsToEuros` llancen `RangeError`
 * amb una entrada invàlida. `toCents`, `checkVatRate` i `vatRateFromPercent` NO llancen mai: tornen
 * `{ ok: false, reason }`. Dins d'un render es valida primer amb aquestes tres.
 */

// ─── 1. Cèntims i tipus ──────────────────────────────────────────────────────

/** Sostre de seguretat propi (999.999,99 €): és per parar un disbarat. */
export const MAX_AMOUNT_CENTS = 99_999_999;

/** 100 % en centèsimes de punt. */
const BP_SCALE = 10_000;
/** Mig cèntim a l'escala de `BP_SCALE`: el «meitat amunt» de `roundedTax`. */
const HALF_SCALE = 5_000;

/**
 * Tipus d'IVA. Un número és un tant per cent ENTER (0, 4, 10, 21…); un tipus amb decimals es
 * passa en centèsimes de punt: 5,2 % és `{ basisPoints: 520 }`. Mai a foc: sempre ve de fora.
 */
export type VatRateInput = number | VatRateBasisPoints;

/** Un tipus ja validat, en centèsimes de punt (10 % = 1000; 5,2 % = 520). */
export interface VatRateBasisPoints {
  readonly basisPoints: number;
}

export type VatRateError =
  | 'MISSING'
  | 'NOT_A_NUMBER'
  | 'NOT_FINITE'
  /** `checkVatRate`: un número amb decimals (5.2) o unes centèsimes de punt no enteres. */
  | 'NOT_AN_INTEGER'
  /** `vatRateFromPercent`: més de dos decimals de tant per cent (5.205). */
  | 'TOO_MANY_DECIMALS'
  | 'OUT_OF_RANGE';

/** El que tornen `checkVatRate` i `vatRateFromPercent`. `rate` es passa tal qual a `splitGross`. */
export type VatRateResult =
  | { ok: true; rate: VatRateBasisPoints }
  | { ok: false; reason: VatRateError };

/** `A` graella forta · `B` exacte només com a total · `C` sense base exacta. */
export type GrossPriceState = 'A' | 'B' | 'C';

export interface VatGridSteps {
  /** El tipus en centèsimes de punt (10 % = 1000). */
  rateBasisPoints: number;
  /** Pas de la base a la graella forta, en cèntims (10 al 10 %, 100 al 21 %). */
  baseStepCents: number;
  /** Pas del PVP a la graella forta, en cèntims (11 al 10 %, 121 al 21 %). */
  grossStepCents: number;
}

export interface GrossSplit {
  /** El PVP que s'ha preguntat, en cèntims. */
  grossCents: number;
  /** El tipus en centèsimes de punt (10 % = 1000). */
  rateBasisPoints: number;
  state: GrossPriceState;
  /**
   * Base en cèntims. A `A` i `B` és LA base (només n'hi ha una). A `C` és la més alta que no es
   * passa del PVP: `baseCents + taxCents` queda `shortfallCents` per sota.
   */
  baseCents: number;
  /** Quota de `baseCents`, en cèntims. */
  taxCents: number;
  /** `true` a `A` i `B`: `baseCents + taxCents === grossCents`. `false` a `C`. */
  exact: boolean;
  /** `grossCents − (baseCents + taxCents)`: 0 a `A` i `B`; a `C`, els cèntims que falten (sempre 1). */
  shortfallCents: number;
}

export interface GrossNeighbourPair {
  /** El PVP vàlid més proper estrictament per sota; `null` si no n'hi ha (per sota de 0). */
  below: GrossSplit | null;
  /** El PVP vàlid més proper estrictament per sobre; `null` si passaria de `MAX_AMOUNT_CENTS`. */
  above: GrossSplit | null;
}

export interface GrossNeighbours {
  grossCents: number;
  rateBasisPoints: number;
  /** L'estat del PVP preguntat. */
  state: GrossPriceState;
  /** Veïns de la graella forta: tots dos en estat `A`. */
  strongGrid: GrossNeighbourPair;
  /** Veïns «amb base exacta»: en estat `A` o `B`. */
  exactBase: GrossNeighbourPair;
}

export type CentsError = 'MISSING' | 'NOT_A_NUMBER' | 'NOT_FINITE' | 'NEGATIVE' | 'TOO_LARGE';

export type CentsResult =
  | {
      ok: true;
      /** L'import en cèntims, arrodonit meitat amunt. */
      cents: number;
      /**
       * `true` si l'entrada és un import de dos decimals: queda a menys de 0,00005 cèntims
       * (0,0000005 €) d'un cèntim sencer. Ho és tot el que surt de sumar i restar imports de dos
       * decimals (`preu − dipòsit`), encara que la coma flotant hi deixi soroll. `false` si té un
       * tercer decimal de debò (179.075) o qualsevol decimal real fins al sisè. Vegeu `toCents`.
       */
      exact: boolean;
    }
  | { ok: false; reason: CentsError };

/** Un import vàlid en cèntims: enter segur i no negatiu. */
export function isCents(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function assertAmount(value: unknown, what: string): number {
  if (!isCents(value)) {
    throw new RangeError(`${what}: s'esperava un enter de cèntims no negatiu`);
  }
  if (value > MAX_AMOUNT_CENTS) {
    throw new RangeError(`${what}: passa del sostre de ${MAX_AMOUNT_CENTS} cèntims`);
  }
  return value + 0; // «+ 0» converteix un -0 en 0
}

function checkInteger(value: unknown, max: number): VatRateError | null {
  if (value === null || value === undefined) return 'MISSING';
  if (typeof value !== 'number' || Number.isNaN(value)) return 'NOT_A_NUMBER';
  if (!Number.isFinite(value)) return 'NOT_FINITE';
  if (!Number.isSafeInteger(value)) return 'NOT_AN_INTEGER';
  if (value < 0 || value > max) return 'OUT_OF_RANGE';
  return null;
}

/**
 * Valida un tipus SENSE llançar mai: és la que es crida dins d'un render o en llegir configuració.
 *
 * Accepta el mateix que `splitGross`: un número (tant per cent ENTER de 0 a 100) o
 * `{ basisPoints }` (enter de 0 a 10000). Un número amb decimals (5.2) és `NOT_AN_INTEGER`: per
 * a això hi ha `vatRateFromPercent`. Si és vàlid, `rate` es pot passar tal qual a `splitGross` i
 * a `nearestOnGrid`, que ja no llançaran pel tipus.
 */
export function checkVatRate(rate: unknown): VatRateResult {
  if (rate === null || rate === undefined) return { ok: false, reason: 'MISSING' };
  if (typeof rate === 'number') {
    const reason = checkInteger(rate, 100);
    return reason === null
      ? { ok: true, rate: { basisPoints: rate * 100 + 0 } } // «+ 0» converteix un -0 en 0
      : { ok: false, reason };
  }
  if (typeof rate !== 'object' || Array.isArray(rate)) return { ok: false, reason: 'NOT_A_NUMBER' };
  const bp: unknown = (rate as { basisPoints?: unknown }).basisPoints;
  const reason = checkInteger(bp, BP_SCALE);
  return reason === null
    ? { ok: true, rate: { basisPoints: (bp as number) + 0 } }
    : { ok: false, reason };
}

function toBasisPoints(rate: VatRateInput): number {
  const checked = checkVatRate(rate);
  // «=== false» i no «!checked.ok»: el backend compila sense `strictNullChecks`, i allà la negació
  // no estreny la unió (TS2339 a `reason`). Amb la comparació estreny a totes dues configuracions.
  if (checked.ok === false) {
    throw new RangeError(
      `tipus no vàlid (${checked.reason}): un número ha de ser un tant per cent enter de 0 a 100; ` +
        `un tipus amb decimals es passa com a { basisPoints } (centèsimes de punt, de 0 a ${BP_SCALE})`,
    );
  }
  return checked.rate.basisPoints;
}

/** Quocient enter de dos enters no negatius. Divideix sempre un múltiple exacte de `b`. */
function divFloor(a: number, b: number): number {
  return (a - (a % b)) / b;
}

function gcd(a: number, b: number): number {
  let x = a;
  let y = b;
  while (y !== 0) {
    const rest = x % y;
    x = y;
    y = rest;
  }
  return x;
}

// ─── 2. La regla d'arrodoniment (un sol punt) ────────────────────────────────

/**
 * Quota d'una base: `r(base × tipus)`, arrodonida al cèntim, MEITAT AMUNT.
 *
 * ⚠️ ÉS L'ÚNIC PUNT ON VIU EL MODEL DE CUENTICA, I EL MODEL ÉS DEDUÏT, NO PROVAT. La spec (§2.1)
 * suposa que Cuentica calcula la quota sobre la base TOTAL de cada tipus i l'arrodoneix meitat
 * amunt. La fase F0 (prova a l'empresa de proves de Cuentica) ho ha de confirmar o corregir. Si ho
 * corregeix, canvia AQUESTA funció i les taules de `vat-split.spec.ts`; `splitGross` i
 * `nearestOnGrid` no saben com s'arrodoneix: només demanen que la quota no baixi quan puja la base.
 */
function roundedTax(baseCents: number, bp: number): number {
  return divFloor(baseCents * bp + HALF_SCALE, BP_SCALE);
}

/** Quota d'una base en cèntims amb la regla d'arrodoniment de `roundedTax`. */
export function vatOnBase(baseCents: number, rate: VatRateInput): number {
  return roundedTax(assertAmount(baseCents, 'baseCents'), toBasisPoints(rate));
}

// ─── 3. La graella i els tres estats ─────────────────────────────────────────

function stepsFor(bp: number): VatGridSteps {
  const g = gcd(bp, BP_SCALE);
  return {
    rateBasisPoints: bp,
    baseStepCents: divFloor(BP_SCALE, g),
    grossStepCents: divFloor(BP_SCALE + bp, g),
  };
}

/**
 * Passos de la graella forta d'un tipus. Per a un tipus enter `t`: base cada `100 ÷ mcd(t, 100)`
 * cèntims i PVP cada `(100 + t) ÷ mcd(t, 100)` cèntims (0,11 € al 10 %: la regla de l'11).
 */
export function vatGridSteps(rate: VatRateInput): VatGridSteps {
  return stepsFor(toBasisPoints(rate));
}

function split(gross: number, bp: number): GrossSplit {
  // Punt de partida: la base «teòrica» per sota. El total creix amb la base, de manera que les
  // dues passes següents la deixen en la base més alta que no es passa del PVP.
  let base = divFloor(gross * BP_SCALE, BP_SCALE + bp);
  // DEFENSA, AVUI CODI MORT: amb «meitat amunt» la base teòrica mai es passa del PVP (la quota
  // arrodonida supera la real en mig cèntim com a molt, i tot són enters), de manera que aquest
  // bucle no fa mai cap volta i cap test el pot fer córrer. Es queda perquè `split` no depengui de
  // com arrodoneix `roundedTax`: si la F0 canvia la regla (p. ex. sempre amunt), aquí es corregeix.
  while (base > 0 && base + roundedTax(base, bp) > gross) base -= 1;
  while (base + 1 + roundedTax(base + 1, bp) <= gross) base += 1;

  const tax = roundedTax(base, bp);
  const shortfall = gross - (base + tax);
  const onGrid = gross % stepsFor(bp).grossStepCents === 0;
  const state: GrossPriceState = shortfall !== 0 ? 'C' : onGrid ? 'A' : 'B';
  return {
    grossCents: gross,
    rateBasisPoints: bp,
    state,
    baseCents: base,
    taxCents: tax,
    exact: shortfall === 0,
    shortfallCents: shortfall,
  };
}

/**
 * Parteix un PVP en base i quota i diu en quin estat és (`A`, `B` o `C`).
 *
 * A `C` no hi ha cap base que doni el PVP: es torna la més alta que no s'hi passa, amb
 * `exact: false` i `shortfallCents` (els cèntims que falten). No arrodoneix ni mou el preu:
 * els preus bons del costat els dona `nearestOnGrid`.
 *
 * @param grossCents PVP en cèntims enters, de 0 a `MAX_AMOUNT_CENTS`.
 * @param rate Tipus d'IVA (enter de 0 a 100, o `{ basisPoints }`).
 * @throws RangeError si l'import o el tipus no són vàlids.
 */
export function splitGross(grossCents: number, rate: VatRateInput): GrossSplit {
  return split(assertAmount(grossCents, 'grossCents'), toBasisPoints(rate));
}

/**
 * Els dos veïns d'un PVP: el preu vàlid més proper per sota i per sobre, tant de la graella forta
 * (`strongGrid`, estat `A`) com «amb base exacta» (`exactBase`, estat `A` o `B`).
 *
 * Són estrictes: un PVP que ja és bo no es torna a si mateix. Qui crea la reserva tria; aquesta
 * funció no en recomana cap.
 *
 * @throws RangeError si l'import o el tipus no són vàlids.
 */
export function nearestOnGrid(grossCents: number, rate: VatRateInput): GrossNeighbours {
  const gross = assertAmount(grossCents, 'grossCents');
  const bp = toBasisPoints(rate);
  const step = stepsFor(bp).grossStepCents;

  const rest = gross % step;
  const gridBelow = gross - (rest === 0 ? step : rest);
  const gridAbove = gross - rest + step;

  let exactBelow: GrossSplit | null = null;
  for (let p = gross - 1; p >= 0 && exactBelow === null; p -= 1) {
    const candidate = split(p, bp);
    if (candidate.exact) exactBelow = candidate;
  }
  let exactAbove: GrossSplit | null = null;
  for (let p = gross + 1; p <= MAX_AMOUNT_CENTS && exactAbove === null; p += 1) {
    const candidate = split(p, bp);
    if (candidate.exact) exactAbove = candidate;
  }

  return {
    grossCents: gross,
    rateBasisPoints: bp,
    state: split(gross, bp).state,
    strongGrid: {
      below: gridBelow >= 0 ? split(gridBelow, bp) : null,
      above: gridAbove <= MAX_AMOUNT_CENTS ? split(gridAbove, bp) : null,
    },
    exactBase: { below: exactBelow, above: exactAbove },
  };
}

// ─── 4. La frontera amb la coma flotant: euros ↔ cèntims i tipus amb decimals ─
// Tot el que hi ha entre les dues marques toca `Float`; la resta del fitxer, mai.

// <float-boundary>
/**
 * Tolerància d'«exacte» a `toCents`, en CÈNTIMS: 0,00005 cèntims = 0,0000005 €. És ABSOLUTA.
 *
 * Per què absoluta: el soroll d'una suma o d'una resta en coma flotant depèn de la mida dels
 * OPERANDS, no de la del resultat. `100 − 90.29` dona 9.709999999999994: un import petit amb el
 * soroll d'un de gran. Un tall relatiu (15 xifres significatives del resultat) el donava per
 * «no exacte», i això rebutjaria un `preu − dipòsit` legítim.
 *
 * Per què aquest número, per dalt (no rebutja cap import net): un import de dos decimals fins al
 * sostre és per sota de 2^20 €, on dos `double` veïns disten 2^-33 €. Cada operand arriba amb mig
 * pas d'error com a molt (2^-34 €) i cada suma o resta n'hi afegeix mig més. Amb `n` operands,
 * l'error és inferior a (2n − 1) × 2^-34 €, és a dir (2n − 1) × 0,0000000058 cèntims, més
 * 0,0000000075 cèntims del producte per 100. Amb `n` = 4.000 són 0,0000466 cèntims: per sota de
 * la tolerància. Garantia: TOT import de dos decimals que surti de fins a 4.000 sumes i restes
 * d'imports de dos decimals, amb tots els passos dins del sostre, surt exacte.
 *
 * Per què aquest número, per baix (no deixa passar cap decimal de més): un valor amb un tercer
 * decimal de debò queda a 0,1 cèntims o més del cèntim sencer més proper, 2.000 vegades la
 * tolerància. Amb un quart decimal, a 0,01; amb un cinquè, a 0,001; amb un sisè, a 0,0001, encara
 * el doble de la tolerància. Cap d'aquests surt exacte.
 *
 * El que SÍ que surt exacte sense ser-ho al bit: un `double` a tocar d'un import net
 * (84.50000000000001), o qualsevol valor a menys de 0,0000005 € d'un cèntim. Són soroll, no
 * decimals: és volgut, i és el preu de no rebutjar les restes.
 */
const CENT_TOLERANCE = 0.00005;

/**
 * Tolerància de `vatRateFromPercent`, en centèsimes de punt. Un tipus de configuració és un
 * literal (5.2), no el resultat d'una cadena de càlculs: multiplicat per 100 s'aparta de l'enter
 * menys d'una bilionèsima. Qualsevol decimal real de tant per cent, del tercer al novè, queda fora.
 */
const RATE_TOLERANCE = 0.00000005;

/**
 * L'ÚNICA conversió d'euros (el `Float` de la reserva) a cèntims. No llança mai.
 *
 * `cents` és l'import arrodonit al cèntim, MEITAT AMUNT. `exact` diu si l'entrada era un import
 * de dos decimals: `true` si queda a `CENT_TOLERANCE` o menys d'un cèntim sencer (vegeu-ne la
 * justificació a la constant). Així 68.2 → 6820, 0.1 + 0.2 → 30 i 100 − 90.29 → 971, tots
 * exactes; i 179.075 → 17908, 1.005 → 101 i 0.001 → 0, cap d'exacte. Qui crida decideix què fa
 * amb un `exact: false`; aquí no es perd en silenci.
 *
 * Mig cèntim: un valor a tocar de la meitat (dins de la tolerància) puja, perquè un 1.005 escrit
 * a mà arriba com a 1.00499999999999989 i no es pot distingir del seu veí. Per tant
 * 0.004999999999999999 dona 1 cèntim, mai exacte.
 *
 * Negatius: un zero amb soroll negatiu d'una resta (0.3 − (0.1 + 0.2), dins de la tolerància) és
 * un 0 exacte. Qualsevol negatiu de debò, per petit que sigui (−0.004), és `NEGATIVE`: mai passa
 * per zero.
 *
 * ⚠️ JA NO és igual que `toCents` del nucli de cobraments (`modules/payments/payments.core.ts`,
 * branca `stripe-payment-links`): aquell talla a 15 xifres significatives (dona `exact: false` a
 * 100 − 90.29) i torna un zero per a un negatiu de menys de mig cèntim. El nom, el tipus del
 * resultat i `cents` per a tot import de dos decimals són els mateixos; canvien `exact` i els
 * negatius. Quan es fusionin, n'ha de quedar una de sola: aquesta.
 */
export function toCents(amount: unknown): CentsResult {
  if (amount === null || amount === undefined) return { ok: false, reason: 'MISSING' };
  if (typeof amount !== 'number' || Number.isNaN(amount)) {
    return { ok: false, reason: 'NOT_A_NUMBER' };
  }
  if (!Number.isFinite(amount)) return { ok: false, reason: 'NOT_FINITE' };
  const scaled = amount * 100;
  if (scaled < -CENT_TOLERANCE) return { ok: false, reason: 'NEGATIVE' };
  // Meitat amunt, amb la mateixa tolerància: el que és a tocar de la meitat, puja.
  const cents = Math.floor(scaled + 0.5 + CENT_TOLERANCE);
  if (cents > MAX_AMOUNT_CENTS) return { ok: false, reason: 'TOO_LARGE' };
  return { ok: true, cents, exact: Math.abs(scaled - cents) <= CENT_TOLERANCE };
}

/**
 * L'ÚNICA conversió de cèntims a euros, per desar a `Reservation.totalPrice` o ensenyar. El
 * resultat és el `double` més proper al decimal de dos xifres: `toCents` el torna a llegir exacte.
 *
 * @throws RangeError si no és un enter de cèntims vàlid.
 */
export function centsToEuros(cents: number): number {
  return assertAmount(cents, 'cents') / 100;
}

/**
 * D'un tipus escrit com a tant per cent amb decimals (5.2, 10, 21) a centèsimes de punt, perquè
 * qui llegeix la configuració no hagi de fer `Math.round(x * 100)` pel seu compte. No llança mai.
 *
 * Només accepta el que es pot representar: un tant per cent de 0 a 100 amb DOS decimals com a
 * molt (5.2 → 520, 0.07 → 7). 5.205 és `TOO_MANY_DECIMALS`; res s'arrodoneix en silenci. És un
 * TANT PER CENT, no una fracció: 0.1 vol dir 0,1 %, no 10 %.
 *
 * `rate` es passa tal qual a `splitGross` i a `nearestOnGrid`.
 */
export function vatRateFromPercent(percent: unknown): VatRateResult {
  if (percent === null || percent === undefined) return { ok: false, reason: 'MISSING' };
  if (typeof percent !== 'number' || Number.isNaN(percent)) {
    return { ok: false, reason: 'NOT_A_NUMBER' };
  }
  if (!Number.isFinite(percent)) return { ok: false, reason: 'NOT_FINITE' };
  const scaled = percent * 100;
  if (scaled < -RATE_TOLERANCE || scaled > BP_SCALE + RATE_TOLERANCE) {
    return { ok: false, reason: 'OUT_OF_RANGE' };
  }
  const basisPoints = Math.round(scaled) + 0; // «+ 0» converteix un -0 en 0
  if (Math.abs(scaled - basisPoints) > RATE_TOLERANCE) {
    return { ok: false, reason: 'TOO_MANY_DECIMALS' };
  }
  return { ok: true, rate: { basisPoints } };
}
// </float-boundary>
